import type { UnitInfoMobileHSIInterface } from 'store/units/types';
import * as go from 'gojs';
import { UnitTypes } from 'unitData/types';
import type { UnitConfigParams } from 'unitData/BaseUnitConfig';
import { BaseUnitConfig } from 'unitData/BaseUnitConfig';
import type { MobileHSIUnitInfoDataInterface } from './unitInfoData';

const RETURN_FLOW_CONFIGURATION_KEYS: Partial<
  Record<string, readonly number[]>
> = {
  QI341: [6, 7, 8],
  QI353: [9, 10, 11, 12, 13, 14],
  QI442: [9, 10, 11, 12, 13, 14],
};

export const canShowReturnFlow = (
  unitInfo: Pick<UnitInfoMobileHSIInterface, 'modelName' | 'configuration'>,
): boolean =>
  RETURN_FLOW_CONFIGURATION_KEYS[unitInfo.modelName]?.includes(
    unitInfo.configuration.configKey,
  ) ?? false;

export class MobileHSIConfig extends BaseUnitConfig {
  static getDefaultData(params?: UnitConfigParams): UnitInfoMobileHSIInterface {
    const unitInfoData =
      (params?.data as MobileHSIUnitInfoDataInterface | undefined) ?? {};
    const type = params?.unitType ?? UnitTypes.MOBILE_TRACKED_HSI;
    if (type === UnitTypes.MOBILE_TRACKED_HSI) {
      const defaultModel = Object.entries(unitInfoData)[3];
      const model = defaultModel[0];
      const modelData = defaultModel[1];

      const hsiData = Object.values(modelData.configurations.HSI1.models)[0];

      return {
        ...BaseUnitConfig.getBaseDefaults(),
        modelName: model,
        configuration: modelData.configurations.configurationOptions[0],
        numberOfPorts: 1,
        moisture: 0,
        newMoisture: false,
        HSI1: {
          modelName: hsiData.simulationModel,
          setting: hsiData.defaultSetting,
          motorFrequency: hsiData.defaultMotorFrequency,
          motorPower: hsiData.defaultMotorPower,
          tipSpeed: 29.1,
        },
        SCREEN1: {
          decks: {
            0: {
              separationSize:
                unitInfoData[model].configurations.SCREEN1.decks[0]
                  .defaultSeparationSize,
            },
            1: {
              separationSize:
                unitInfoData[model].configurations.SCREEN1.decks[1]
                  .defaultSeparationSize,
            },
          },
        },
        SCREEN2: {
          decks: {
            0: {
              separationSize:
                unitInfoData[model].configurations.SCREEN2.decks[0]
                  .defaultSeparationSize,
            },
            1: {
              separationSize:
                unitInfoData[model]?.configurations?.SCREEN2?.decks[1]
                  ?.defaultSeparationSize || 25,
            },
          },
        },
        display: {
          ...BaseUnitConfig.getDefaultDisplay(),
          displayConfig: false,
          displayLoad: true,
          displayFeederModel: false,
          displayFeederSeparation: false,
          displayCrusherModel: false,
          displayCrusherSetting: false,
          displayCrusherFrequency: false,
          displayCrusherMotorPower: false,
          displayCrusherTipSpeed: false,
          displayPanFeederModel: false,
          displayScreenSeparation: false,
          displayReturnFlow: false,
        },
        parallelUnits: 1,
      };
    } else {
      const defaultModel = Object.entries(unitInfoData)[0];
      const model = defaultModel[0];
      const modelData = defaultModel[1];

      const hsiData = Object.values(modelData.configurations.HSI1.models)[0];

      return {
        ...BaseUnitConfig.getBaseDefaults(),
        modelName: model,
        configuration: modelData.configurations.configurationOptions[0],
        numberOfPorts: 2,
        moisture: 0,
        newMoisture: false,
        HSI1: {
          modelName: hsiData.simulationModel,
          setting: hsiData.defaultSetting,
          motorFrequency: hsiData.defaultMotorFrequency,
          motorPower: hsiData.defaultMotorPower,
          tipSpeed: 33.4,
        },
        SCREEN1: {
          decks: {
            0: {
              separationSize:
                unitInfoData[model].configurations.SCREEN1.decks[0]
                  .defaultSeparationSize,
            },
            1: {
              separationSize:
                unitInfoData[model].configurations.SCREEN1.decks[1]
                  .defaultSeparationSize,
            },
          },
        },
        SCREEN2: {
          decks: {
            0: {
              separationSize: 0,
            },
            1: {
              separationSize: 0,
            },
          },
        },
        display: {
          ...BaseUnitConfig.getDefaultDisplay(),
          displayConfig: false,
          displayLoad: true,
          displayFeederModel: false,
          displayFeederSeparation: false,
          displayCrusherModel: false,
          displayCrusherSetting: false,
          displayCrusherFrequency: false,
          displayCrusherMotorPower: false,
          displayCrusherTipSpeed: false,
          displayPanFeederModel: false,
          displayScreenSeparation: false,
          displayReturnFlow: false,
        },
        parallelUnits: 1,
      };
    }
  }

  static getNumberOfPorts(model?: string, config?: number): number {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return getNumberOfPorts(model!, config!);
  }
}

export const mobileHSIConfig = new MobileHSIConfig();

export const getNumberOfPorts = (model: string, configKey: number) => {
  const isKey = (...keys: number[]) => keys.includes(configKey);
  switch (model) {
    case 'QI341':
      if (isKey(0, 6)) {
        return 1;
      } else if (isKey(1, 2, 3, 7, 8)) {
        return 2;
      }

      return 3;
    case 'UI310+':
      if (isKey(0, 2)) {
        return 2;
      }
      return 1;
    default:
      if (isKey(0, 9)) {
        return 1;
      } else if (isKey(1, 2, 3, 10, 11, 13)) {
        return 2;
      } else if (isKey(4, 5, 7, 12, 14)) {
        return 3;
      }
      return 4;
  }
};

export const alignPorts = (
  mirrored: boolean,
  model: string,
  configKey: number,
  portIndex: number,
) => {
  const getXMirrored = (x: number) => {
    if (mirrored) {
      return 1 - x;
    }
    return x;
  };

  if (model === 'QI341') {
    const positionOne = new go.Spot(getXMirrored(7 / 12), 0);
    const positionTwo = new go.Spot(getXMirrored(1), 8 / 12);
    const positionThree = new go.Spot(getXMirrored(1), 10 / 12);

    switch (configKey) {
      case 0:
        switch (portIndex) {
          case 0:
            return positionThree;
        }
        break;
      case 1:
        switch (portIndex) {
          case 0:
            return positionOne;
          case 1:
            return positionThree;
        }
        break;
      case 2:
        switch (portIndex) {
          case 0:
            return positionOne;
          case 1:
            return positionThree;
        }
        break;
      case 3:
        switch (portIndex) {
          case 0:
            return positionTwo;
          case 1:
            return positionThree;
        }
        break;
      case 4:
        switch (portIndex) {
          case 0:
            return positionOne;
          case 1:
            return positionTwo;
          case 2:
            return positionThree;
        }
        break;
      case 5:
        switch (portIndex) {
          case 0:
            return positionOne;
          case 1:
            return positionTwo;
          case 2:
            return positionThree;
        }
        break;
      case 6:
        switch (portIndex) {
          case 0:
            return positionThree;
        }
        break;
      case 7:
        switch (portIndex) {
          case 0:
            return positionOne;
          case 1:
            return positionThree;
        }
        break;
      case 8:
        switch (portIndex) {
          case 0:
            return positionOne;
          case 1:
            return positionThree;
        }
        break;
    }
  } else if (model === 'UI310+') {
    const positionOne = new go.Spot(getXMirrored(0), 8 / 12);
    const positionTwo = new go.Spot(getXMirrored(1), 8 / 12);

    switch (configKey) {
      case 0:
      case 2:
        switch (portIndex) {
          case 0:
            return positionOne;
          case 1:
            return positionTwo;
        }
        break;
      case 1:
      case 3:
        return positionTwo;
    }
  } else {
    const positionOne = new go.Spot(getXMirrored(6 / 12), 0);
    const positionTwo = new go.Spot(getXMirrored(1), 2 / 12);
    const positionThree = new go.Spot(getXMirrored(1), 11 / 12);
    const positionFour = new go.Spot(getXMirrored(5 / 12), 1);

    switch (configKey) {
      case 0:
        switch (portIndex) {
          case 0:
            return positionThree;
        }
        break;
      case 1:
        switch (portIndex) {
          case 0:
            return positionOne;
          case 1:
            return positionThree;
        }
        break;
      case 2:
        switch (portIndex) {
          case 0:
            return positionOne;
          case 1:
            return positionThree;
        }
        break;
      case 3:
        switch (portIndex) {
          case 0:
            return positionTwo;
          case 1:
            return positionThree;
        }
        break;
      case 4:
        switch (portIndex) {
          case 0:
            return positionTwo;
          case 1:
            return positionFour;
          case 2:
            return positionThree;
        }
        break;
      case 5:
        switch (portIndex) {
          case 0:
            return positionOne;
          case 1:
            return positionTwo;
          case 2:
            return positionThree;
        }
        break;
      case 6:
        switch (portIndex) {
          case 0:
            return positionOne;
          case 1:
            return positionTwo;
          case 2:
            return positionFour;
          case 3:
            return positionThree;
        }
        break;
      case 7:
        switch (portIndex) {
          case 0:
            return positionOne;
          case 1:
            return positionTwo;
          case 2:
            return positionThree;
        }
        break;
      case 8:
        switch (portIndex) {
          case 0:
            return positionOne;
          case 1:
            return positionTwo;
          case 2:
            return positionFour;
          case 3:
            return positionThree;
        }
        break;
      case 9:
        switch (portIndex) {
          case 0:
            return positionThree;
        }
        break;
      case 10:
        switch (portIndex) {
          case 0:
            return positionFour;
          case 1:
            return positionThree;
        }
        break;
      case 11:
        switch (portIndex) {
          case 0:
            return positionOne;
          case 1:
            return positionThree;
        }
        break;
      case 12:
        switch (portIndex) {
          case 0:
            return positionOne;
          case 1:
            return positionFour;
          case 2:
            return positionThree;
        }
        break;
      case 13:
        switch (portIndex) {
          case 0:
            return positionOne;
          case 1:
            return positionThree;
        }
        break;
      case 14:
        switch (portIndex) {
          case 0:
            return positionOne;
          case 1:
            return positionFour;
          case 2:
            return positionThree;
        }
        break;
    }
  }
};
